import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, apiFetch } from '@/lib/api';
import { ArrowLeft, Link2, Loader2, NotebookPen, Paperclip, RefreshCw, Save } from 'lucide-react';
import { uploadFile } from '@/lib/attachment-upload';

interface WorkspaceRecord {
  id: string;
  workspace_name: string;
  workspace_type: string;
  stage: string;
  priority: string;
  health_status: string;
  summary: string | null;
  linked_tenant_id?: string | null;
  account_name?: string;
  account_code?: string | null;
  account_lifecycle_stage?: string;
  account_status?: string;
  updated_at: string;
}

interface TeamNote {
  id: string;
  note_type: string;
  title: string;
  body: string;
  created_at: string;
}

interface TeamEvent {
  id: string;
  event_type: string;
  from_stage: string | null;
  to_stage: string | null;
  note: string | null;
  created_at: string;
}

interface TeamAttachment {
  id: string;
  doc_type: string | null;
  file_name: string;
  file_url: string;
  mime_type: string | null;
  created_at: string;
}

interface TeamObjectLink {
  id: string;
  link_type: string;
  object_source: string;
  object_type: string;
  object_id: string;
  title: string | null;
  status: string | null;
  url: string | null;
  created_at: string;
}

interface TeamCommercialPlan {
  id: string;
  status: string;
  currency: string;
  scope_summary: string | null;
  pricing_model: string | null;
  onboarding_fee: string | number | null;
  monthly_run_rate: string | number | null;
  implementation_estimate: string | number | null;
  credits_assumption: string | null;
  updated_at: string;
}

interface TeamHandoff {
  id: string;
  handoff_type: string;
  status: string;
  title: string;
  from_owner: string | null;
  to_owner: string | null;
  due_date: string | null;
  completed_at: string | null;
  summary: string | null;
  updated_at: string;
}

interface TeamRuntimeSnapshot {
  id: string;
  snapshot_type: string;
  health_status: string;
  alert_level: string;
  adoption_score: number | null;
  friction_score: number | null;
  summary: string | null;
  recommendations: string[] | null;
  captured_at: string;
}

interface TeamStageGate {
  id: string;
  gate_type: string;
  verdict: string;
  due_date: string | null;
  decided_at: string | null;
  summary: string | null;
  evidence: string[] | null;
  updated_at: string;
}

interface WorkspaceDetail {
  workspace: WorkspaceRecord;
  notes: TeamNote[];
  events: TeamEvent[];
  attachments: TeamAttachment[];
  objectLinks: TeamObjectLink[];
  commercialPlan: TeamCommercialPlan | null;
  handoffs: TeamHandoff[];
  runtimeSnapshots: TeamRuntimeSnapshot[];
  stageGates: TeamStageGate[];
}

const STAGES = ['PROSPECT', 'QUALIFIED', 'SOLUTIONING', 'PM_HANDOVER', 'IMPLEMENTATION', 'UAT_GOLIVE', 'HYPERCARE'] as const;
const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
const HEALTH = ['healthy', 'watch', 'risk', 'critical'] as const;
const NOTE_TYPES = ['general', 'meeting', 'decision', 'handoff', 'risk', 'follow_up'] as const;
const COMMERCIAL_STATUSES = ['draft', 'proposed', 'approved', 'signed', 'archived'] as const;
const HANDOFF_TYPES = [
  'sales_to_pm',
  'pm_to_impl',
  'impl_to_rollout',
  'rollout_to_hypercare',
  'sample_request',
  'activation_planning',
  'migration_planning',
  'ai_activation',
  'finance_activation',
  'audit_activation',
  'matchbox_activation',
  'matchbox_credentials',
  'matchbox_billing',
  'custom',
] as const;
const HANDOFF_STATUSES = ['draft', 'ready', 'completed', 'blocked'] as const;
const SNAPSHOT_TYPES = ['manual', 'adoption', 'friction', 'alert_digest', 'health_check'] as const;
const ALERT_LEVELS = ['normal', 'watch', 'risk', 'critical'] as const;
const GATE_TYPES = [
  'discovery_exit',
  'solutioning_exit',
  'pm_handover',
  'implementation_ready',
  'uat_signoff',
  'go_live',
  'hypercare_exit',
  'sample_pack_received',
  'capability_map_confirmed',
  'first_workflow_activation_ready',
  'migration_scope_confirmed',
  'ai_intake_control_gate',
  'ai_billing_activation_gate',
  'internal_audit_activation_gate',
  'matchbox_credentials_confirmed',
  'hms_sample_jobs_received',
  'hms_matchbox_field_mapping_ready',
  'matchbox_dry_run_eligibility_ready',
  'matchbox_create_reuse_approval_gate',
  'matchbox_billing_rules_confirmed',
  'custom',
] as const;
const GATE_VERDICTS = ['pending', 'pass', 'fail', 'watch'] as const;

function isOpenableUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/');
}

function formatAttachmentUrl(url: string): string {
  return url.startsWith('repo://') ? url.slice('repo://'.length) : url;
}

export default function TeamspaceWorkspacePage() {
  const { id = '' } = useParams();
  const [detail, setDetail] = useState<WorkspaceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [workspaceForm, setWorkspaceForm] = useState({
    stage: 'PROSPECT',
    priority: 'normal',
    healthStatus: 'healthy',
    summary: '',
    note: '',
  });
  const [noteForm, setNoteForm] = useState({
    noteType: 'general',
    title: '',
    body: '',
  });
  const [attachmentSaving, setAttachmentSaving] = useState(false);
  const [attachmentForm, setAttachmentForm] = useState({
    docType: 'workspace_brief',
    fileName: '',
    fileUrl: '',
    mimeType: '',
  });
  const [pendingAttachmentFile, setPendingAttachmentFile] = useState<File | null>(null);
  const [linkSaving, setLinkSaving] = useState(false);
  const [syncingLinkedObjects, setSyncingLinkedObjects] = useState(false);
  const [refreshingRuntime, setRefreshingRuntime] = useState(false);
  const [commercialSaving, setCommercialSaving] = useState(false);
  const [handoffSaving, setHandoffSaving] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [gateSaving, setGateSaving] = useState(false);
  const [objectLinkForm, setObjectLinkForm] = useState({
    linkType: 'onboarding_record',
    objectSource: 'tenant',
    objectType: '',
    objectId: '',
    title: '',
    status: '',
    url: '',
  });
  const [commercialForm, setCommercialForm] = useState({
    status: 'draft',
    currency: 'SGD',
    scopeSummary: '',
    pricingModel: '',
    onboardingFee: '',
    monthlyRunRate: '',
    implementationEstimate: '',
    creditsAssumption: '',
  });
  const [handoffForm, setHandoffForm] = useState({
    handoffType: 'sales_to_pm',
    status: 'draft',
    title: '',
    fromOwner: '',
    toOwner: '',
    dueDate: '',
    summary: '',
  });
  const [snapshotForm, setSnapshotForm] = useState({
    snapshotType: 'manual',
    healthStatus: 'healthy',
    alertLevel: 'normal',
    adoptionScore: '',
    frictionScore: '',
    summary: '',
    recommendations: '',
  });
  const [gateForm, setGateForm] = useState({
    gateType: 'discovery_exit',
    verdict: 'pending',
    dueDate: '',
    summary: '',
    evidence: '',
  });

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const data = await apiFetch<WorkspaceDetail>(`/teamspace/workspaces/${encodeURIComponent(id)}`);
      setDetail(data);
      setWorkspaceForm({
        stage: data.workspace.stage,
        priority: data.workspace.priority,
        healthStatus: data.workspace.health_status,
        summary: data.workspace.summary ?? '',
        note: '',
      });
      setCommercialForm({
        status: data.commercialPlan?.status ?? 'draft',
        currency: data.commercialPlan?.currency ?? 'SGD',
        scopeSummary: data.commercialPlan?.scope_summary ?? '',
        pricingModel: data.commercialPlan?.pricing_model ?? '',
        onboardingFee: data.commercialPlan?.onboarding_fee != null ? String(data.commercialPlan.onboarding_fee) : '',
        monthlyRunRate: data.commercialPlan?.monthly_run_rate != null ? String(data.commercialPlan.monthly_run_rate) : '',
        implementationEstimate: data.commercialPlan?.implementation_estimate != null ? String(data.commercialPlan.implementation_estimate) : '',
        creditsAssumption: data.commercialPlan?.credits_assumption ?? '',
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [id]);

  async function submitWorkspaceUpdate(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    try {
      await api.patch(`/teamspace/workspaces/${encodeURIComponent(id)}`, {
        stage: workspaceForm.stage,
        priority: workspaceForm.priority,
        healthStatus: workspaceForm.healthStatus,
        summary: workspaceForm.summary || null,
        note: workspaceForm.note || undefined,
      });
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function submitNote(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setNoteSaving(true);
    try {
      await api.post(`/teamspace/workspaces/${encodeURIComponent(id)}/notes`, {
        noteType: noteForm.noteType,
        title: noteForm.title,
        body: noteForm.body,
      });
      setNoteForm({ noteType: 'general', title: '', body: '' });
      await load();
    } finally {
      setNoteSaving(false);
    }
  }

  async function submitAttachment(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setAttachmentSaving(true);
    try {
      let payload = {
        docType: attachmentForm.docType || undefined,
        fileName: attachmentForm.fileName,
        fileUrl: attachmentForm.fileUrl,
        mimeType: attachmentForm.mimeType || undefined,
        metadata: undefined as Record<string, unknown> | undefined,
      };

      if (pendingAttachmentFile) {
        const uploaded = await uploadFile(pendingAttachmentFile, 'workspace', id);
        payload = {
          docType: attachmentForm.docType || undefined,
          fileName: uploaded.fileName,
          fileUrl: uploaded.fileUrl,
          mimeType: uploaded.mimeType || undefined,
          metadata: uploaded.fileId ? { fileShareFileId: uploaded.fileId } : undefined,
        };
      }

      await api.post(`/teamspace/workspaces/${encodeURIComponent(id)}/attachments`, {
        source: pendingAttachmentFile ? 'upload' : 'link',
        docType: payload.docType,
        fileName: payload.fileName,
        fileUrl: payload.fileUrl,
        mimeType: payload.mimeType,
        metadata: payload.metadata,
      });
      setAttachmentForm({ docType: 'workspace_brief', fileName: '', fileUrl: '', mimeType: '' });
      setPendingAttachmentFile(null);
      await load();
    } finally {
      setAttachmentSaving(false);
    }
  }

  async function submitObjectLink(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setLinkSaving(true);
    try {
      await api.post(`/teamspace/workspaces/${encodeURIComponent(id)}/object-links`, {
        linkType: objectLinkForm.linkType,
        objectSource: objectLinkForm.objectSource,
        objectType: objectLinkForm.objectType,
        objectId: objectLinkForm.objectId,
        title: objectLinkForm.title || undefined,
        status: objectLinkForm.status || undefined,
        url: objectLinkForm.url || undefined,
      });
      setObjectLinkForm({
        linkType: 'onboarding_record',
        objectSource: 'tenant',
        objectType: '',
        objectId: '',
        title: '',
        status: '',
        url: '',
      });
      await load();
    } finally {
      setLinkSaving(false);
    }
  }

  async function syncLinkedObjects() {
    if (!id) return;
    setSyncingLinkedObjects(true);
    try {
      await api.post(`/teamspace/workspaces/${encodeURIComponent(id)}/sync-linked-objects`, {});
      await load();
    } finally {
      setSyncingLinkedObjects(false);
    }
  }

  async function refreshRuntimeIntelligence() {
    if (!id) return;
    setRefreshingRuntime(true);
    try {
      await api.post(`/teamspace/workspaces/${encodeURIComponent(id)}/refresh-runtime-intelligence`, {});
      await load();
    } finally {
      setRefreshingRuntime(false);
    }
  }

  async function submitCommercialPlan(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setCommercialSaving(true);
    try {
      await api.post(`/teamspace/workspaces/${encodeURIComponent(id)}/commercial-plan`, {
        status: commercialForm.status,
        currency: commercialForm.currency,
        scopeSummary: commercialForm.scopeSummary || undefined,
        pricingModel: commercialForm.pricingModel || undefined,
        onboardingFee: commercialForm.onboardingFee ? Number(commercialForm.onboardingFee) : undefined,
        monthlyRunRate: commercialForm.monthlyRunRate ? Number(commercialForm.monthlyRunRate) : undefined,
        implementationEstimate: commercialForm.implementationEstimate ? Number(commercialForm.implementationEstimate) : undefined,
        creditsAssumption: commercialForm.creditsAssumption || undefined,
      });
      await load();
    } finally {
      setCommercialSaving(false);
    }
  }

  async function submitHandoff(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setHandoffSaving(true);
    try {
      await api.post(`/teamspace/workspaces/${encodeURIComponent(id)}/handoffs`, {
        handoffType: handoffForm.handoffType,
        status: handoffForm.status,
        title: handoffForm.title,
        fromOwner: handoffForm.fromOwner || undefined,
        toOwner: handoffForm.toOwner || undefined,
        dueDate: handoffForm.dueDate || undefined,
        summary: handoffForm.summary || undefined,
      });
      setHandoffForm({
        handoffType: 'sales_to_pm',
        status: 'draft',
        title: '',
        fromOwner: '',
        toOwner: '',
        dueDate: '',
        summary: '',
      });
      await load();
    } finally {
      setHandoffSaving(false);
    }
  }

  async function submitRuntimeSnapshot(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSnapshotSaving(true);
    try {
      await api.post(`/teamspace/workspaces/${encodeURIComponent(id)}/runtime-snapshots`, {
        snapshotType: snapshotForm.snapshotType,
        healthStatus: snapshotForm.healthStatus,
        alertLevel: snapshotForm.alertLevel,
        adoptionScore: snapshotForm.adoptionScore ? Number(snapshotForm.adoptionScore) : undefined,
        frictionScore: snapshotForm.frictionScore ? Number(snapshotForm.frictionScore) : undefined,
        summary: snapshotForm.summary || undefined,
        recommendations: snapshotForm.recommendations
          .split('\n')
          .map((item) => item.trim())
          .filter(Boolean),
      });
      setSnapshotForm({
        snapshotType: 'manual',
        healthStatus: 'healthy',
        alertLevel: 'normal',
        adoptionScore: '',
        frictionScore: '',
        summary: '',
        recommendations: '',
      });
      await load();
    } finally {
      setSnapshotSaving(false);
    }
  }

  async function submitStageGate(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setGateSaving(true);
    try {
      await api.post(`/teamspace/workspaces/${encodeURIComponent(id)}/stage-gates`, {
        gateType: gateForm.gateType,
        verdict: gateForm.verdict,
        dueDate: gateForm.dueDate || undefined,
        summary: gateForm.summary || undefined,
        evidence: gateForm.evidence
          .split('\n')
          .map((item) => item.trim())
          .filter(Boolean),
      });
      setGateForm({
        gateType: 'discovery_exit',
        verdict: 'pending',
        dueDate: '',
        summary: '',
        evidence: '',
      });
      await load();
    } finally {
      setGateSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="px-4 py-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
          Workspace not found.
        </div>
      </div>
    );
  }

  const { workspace, notes, events, handoffs, runtimeSnapshots, stageGates, commercialPlan } = detail;

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <Link to="/admin/teamspace" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" />
            Back to Teamspace
          </Link>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">{workspace.workspace_name}</h1>
          <p className="text-sm text-slate-500">
            {workspace.account_name ?? 'Unlinked account'} · {workspace.workspace_type} · Updated {new Date(workspace.updated_at).toLocaleString('en-SG')}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => void syncLinkedObjects()}
            disabled={syncingLinkedObjects || !workspace.linked_tenant_id}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${syncingLinkedObjects ? 'animate-spin' : ''}`} />
            Sync linked tenant objects
          </button>
          <button
            type="button"
            onClick={() => void refreshRuntimeIntelligence()}
            disabled={refreshingRuntime}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshingRuntime ? 'animate-spin' : ''}`} />
            Auto refresh runtime
          </button>
        </div>
      </div>

      <section className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Stage" value={workspace.stage} />
        <MetricCard label="Health" value={workspace.health_status} tone="bg-amber-50 text-amber-950" />
        <MetricCard label="Priority" value={workspace.priority} tone="bg-blue-50 text-blue-950" />
        <MetricCard label="Account" value={workspace.account_name ?? '-'} tone="bg-slate-50 text-slate-900" />
      </section>

      <section className="grid gap-6 xl:grid-cols-[1fr_1fr]">
        <form onSubmit={(e) => void submitWorkspaceUpdate(e)} className="rounded-3xl border border-slate-200 bg-white p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Workspace control</h2>
            <p className="mt-1 text-sm text-slate-500">更新当前 workspace 的 stage、priority、health 和 summary。</p>
          </div>
          <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={workspaceForm.stage} onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, stage: e.target.value }))}>
            {STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
          </select>
          <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={workspaceForm.priority} onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, priority: e.target.value }))}>
            {PRIORITIES.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
          </select>
          <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={workspaceForm.healthStatus} onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, healthStatus: e.target.value }))}>
            {HEALTH.map((health) => <option key={health} value={health}>{health}</option>)}
          </select>
          <textarea className="min-h-[120px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Workspace summary" value={workspaceForm.summary} onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, summary: e.target.value }))} />
          <textarea className="min-h-[88px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Optional event note" value={workspaceForm.note} onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, note: e.target.value }))} />
          <button disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            <Save className="h-4 w-4" />
            Save workspace
          </button>
        </form>

        <form onSubmit={(e) => void submitNote(e)} className="rounded-3xl border border-slate-200 bg-white p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Add note</h2>
            <p className="mt-1 text-sm text-slate-500">记录 meeting note、decision、handoff 或 risk brief。</p>
          </div>
          <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={noteForm.noteType} onChange={(e) => setNoteForm((prev) => ({ ...prev, noteType: e.target.value }))}>
            {NOTE_TYPES.map((noteType) => <option key={noteType} value={noteType}>{noteType}</option>)}
          </select>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Title" value={noteForm.title} onChange={(e) => setNoteForm((prev) => ({ ...prev, title: e.target.value }))} />
          <textarea className="min-h-[188px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Body" value={noteForm.body} onChange={(e) => setNoteForm((prev) => ({ ...prev, body: e.target.value }))} />
          <button disabled={noteSaving || !noteForm.title.trim() || !noteForm.body.trim()} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            <NotebookPen className="h-4 w-4" />
            Add note
          </button>
        </form>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1fr_1fr]">
        <form onSubmit={(e) => void submitCommercialPlan(e)} className="rounded-3xl border border-slate-200 bg-white p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Commercial plan</h2>
            <p className="mt-1 text-sm text-slate-500">收 implementation estimate、onboarding fee、run-rate 和 credits assumption。</p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={commercialForm.status} onChange={(e) => setCommercialForm((prev) => ({ ...prev, status: e.target.value }))}>
              {COMMERCIAL_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Currency" value={commercialForm.currency} onChange={(e) => setCommercialForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }))} />
          </div>
          <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Pricing model" value={commercialForm.pricingModel} onChange={(e) => setCommercialForm((prev) => ({ ...prev, pricingModel: e.target.value }))} />
          <div className="grid gap-3 md:grid-cols-3">
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Onboarding fee" value={commercialForm.onboardingFee} onChange={(e) => setCommercialForm((prev) => ({ ...prev, onboardingFee: e.target.value }))} />
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Monthly run rate" value={commercialForm.monthlyRunRate} onChange={(e) => setCommercialForm((prev) => ({ ...prev, monthlyRunRate: e.target.value }))} />
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Implementation estimate" value={commercialForm.implementationEstimate} onChange={(e) => setCommercialForm((prev) => ({ ...prev, implementationEstimate: e.target.value }))} />
          </div>
          <textarea className="min-h-[120px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Scope summary" value={commercialForm.scopeSummary} onChange={(e) => setCommercialForm((prev) => ({ ...prev, scopeSummary: e.target.value }))} />
          <textarea className="min-h-[120px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Credits assumption" value={commercialForm.creditsAssumption} onChange={(e) => setCommercialForm((prev) => ({ ...prev, creditsAssumption: e.target.value }))} />
          <button disabled={commercialSaving} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            <Save className="h-4 w-4" />
            Save commercial plan
          </button>
          {commercialPlan ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              Last updated {new Date(commercialPlan.updated_at).toLocaleString('en-SG')}
            </div>
          ) : null}
        </form>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Handoffs</h2>
            <p className="mt-1 text-sm text-slate-500">把 owner transfer、readiness 和 blocker 显式化。</p>
          </div>
          <form onSubmit={(e) => void submitHandoff(e)} className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={handoffForm.handoffType} onChange={(e) => setHandoffForm((prev) => ({ ...prev, handoffType: e.target.value }))}>
                {HANDOFF_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={handoffForm.status} onChange={(e) => setHandoffForm((prev) => ({ ...prev, status: e.target.value }))}>
                {HANDOFF_STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </div>
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Handoff title" value={handoffForm.title} onChange={(e) => setHandoffForm((prev) => ({ ...prev, title: e.target.value }))} />
            <div className="grid gap-3 md:grid-cols-3">
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="From owner" value={handoffForm.fromOwner} onChange={(e) => setHandoffForm((prev) => ({ ...prev, fromOwner: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="To owner" value={handoffForm.toOwner} onChange={(e) => setHandoffForm((prev) => ({ ...prev, toOwner: e.target.value }))} />
              <input type="date" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={handoffForm.dueDate} onChange={(e) => setHandoffForm((prev) => ({ ...prev, dueDate: e.target.value }))} />
            </div>
            <textarea className="min-h-[120px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Summary / missing decisions / blockers" value={handoffForm.summary} onChange={(e) => setHandoffForm((prev) => ({ ...prev, summary: e.target.value }))} />
            <button disabled={handoffSaving || !handoffForm.title.trim()} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              <NotebookPen className="h-4 w-4" />
              Add handoff
            </button>
          </form>
          <div className="space-y-3">
            {handoffs.length === 0 ? <EmptyState label="No handoffs yet" /> : handoffs.map((handoff) => (
              <div key={handoff.id} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-slate-900">{handoff.title}</div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{handoff.status}</span>
                </div>
                <div className="mt-2 text-sm text-slate-500">{handoff.handoff_type} · {handoff.from_owner ?? '-'} → {handoff.to_owner ?? '-'}</div>
                {handoff.summary ? <p className="mt-2 text-sm leading-6 text-slate-600">{handoff.summary}</p> : null}
                <div className="mt-3 text-xs text-slate-400">Due {handoff.due_date ?? '-'} · Updated {new Date(handoff.updated_at).toLocaleString('en-SG')}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1fr_1fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-slate-500" />
            <h2 className="text-lg font-semibold text-slate-900">Attachments</h2>
          </div>
          <form onSubmit={(e) => void submitAttachment(e)} className="mt-4 space-y-3">
            <input
              type="file"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setPendingAttachmentFile(file);
                if (file) {
                  setAttachmentForm((prev) => ({
                    ...prev,
                    fileName: file.name,
                    mimeType: file.type || prev.mimeType,
                  }));
                }
              }}
            />
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="File name" value={attachmentForm.fileName} onChange={(e) => setAttachmentForm((prev) => ({ ...prev, fileName: e.target.value }))} />
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="File URL (optional if uploading file)" value={attachmentForm.fileUrl} onChange={(e) => setAttachmentForm((prev) => ({ ...prev, fileUrl: e.target.value }))} />
            <div className="grid gap-3 md:grid-cols-2">
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Doc type" value={attachmentForm.docType} onChange={(e) => setAttachmentForm((prev) => ({ ...prev, docType: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="MIME type" value={attachmentForm.mimeType} onChange={(e) => setAttachmentForm((prev) => ({ ...prev, mimeType: e.target.value }))} />
            </div>
            <button disabled={attachmentSaving || !attachmentForm.fileName.trim() || (!pendingAttachmentFile && !attachmentForm.fileUrl.trim())} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              <Link2 className="h-4 w-4" />
              {pendingAttachmentFile ? 'Upload attachment' : 'Add attachment'}
            </button>
          </form>
          <div className="mt-5 space-y-3">
            {detail.attachments.length === 0 ? <EmptyState label="No attachments yet" /> : detail.attachments.map((attachment) => {
              const openable = isOpenableUrl(attachment.file_url);
              const body = (
                <div className="rounded-2xl border border-slate-200 p-4 transition hover:border-slate-300 hover:bg-slate-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-medium text-slate-900">{attachment.file_name}</div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                      {openable ? 'openable' : 'repo evidence'}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-slate-500">{attachment.doc_type ?? 'attachment'} · {attachment.mime_type ?? 'unknown'}</div>
                  <div className="mt-2 break-all rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-500">{formatAttachmentUrl(attachment.file_url)}</div>
                </div>
              );
              return openable ? (
                <a key={attachment.id} href={attachment.file_url} target="_blank" rel="noreferrer" className="block">
                  {body}
                </a>
              ) : (
                <div key={attachment.id}>{body}</div>
              );
            })}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900">Notes</h2>
          <div className="mt-4 space-y-3">
            {notes.length === 0 ? <EmptyState label="No notes yet" /> : notes.map((note) => (
              <div key={note.id} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-slate-900">{note.title}</div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{note.note_type}</span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-600">{note.body}</p>
                <div className="mt-3 text-xs text-slate-400">{new Date(note.created_at).toLocaleString('en-SG')}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900">Linked lifecycle objects</h2>
          <form onSubmit={(e) => void submitObjectLink(e)} className="mt-4 space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={objectLinkForm.linkType} onChange={(e) => setObjectLinkForm((prev) => ({ ...prev, linkType: e.target.value }))}>
                {['onboarding_record', 'business_project', 'rollout_plan', 'pilot', 'go_live', 'task', 'thread', 'tenant', 'runtime_snapshot', 'custom'].map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={objectLinkForm.objectSource} onChange={(e) => setObjectLinkForm((prev) => ({ ...prev, objectSource: e.target.value }))}>
                {['tenant', 'team', 'global', 'log', 'external'].map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Object type" value={objectLinkForm.objectType} onChange={(e) => setObjectLinkForm((prev) => ({ ...prev, objectType: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Object ID" value={objectLinkForm.objectId} onChange={(e) => setObjectLinkForm((prev) => ({ ...prev, objectId: e.target.value }))} />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Title" value={objectLinkForm.title} onChange={(e) => setObjectLinkForm((prev) => ({ ...prev, title: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Status" value={objectLinkForm.status} onChange={(e) => setObjectLinkForm((prev) => ({ ...prev, status: e.target.value }))} />
            </div>
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Deep link URL (optional)" value={objectLinkForm.url} onChange={(e) => setObjectLinkForm((prev) => ({ ...prev, url: e.target.value }))} />
            <button disabled={linkSaving || !objectLinkForm.objectType.trim() || !objectLinkForm.objectId.trim()} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              <Link2 className="h-4 w-4" />
              Link object
            </button>
          </form>
          <div className="mt-5 space-y-3">
            {detail.objectLinks.length === 0 ? <EmptyState label="No linked objects yet" /> : detail.objectLinks.map((link) => {
              const card = (
                <div className="rounded-2xl border border-slate-200 p-4 transition hover:border-slate-300 hover:bg-slate-50">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium text-slate-900">{link.title ?? `${link.object_type} · ${link.object_id}`}</div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{link.link_type}</span>
                  </div>
                  <div className="mt-2 text-sm text-slate-500">{link.object_source} · {link.object_type} · {link.object_id}</div>
                  {link.status ? <div className="mt-1 text-sm text-slate-500">Status: {link.status}</div> : null}
                  <div className="mt-3 text-xs text-slate-400">{new Date(link.created_at).toLocaleString('en-SG')}</div>
                </div>
              );
              return link.url ? <a key={link.id} href={link.url} target="_blank" rel="noreferrer" className="block">{card}</a> : <div key={link.id}>{card}</div>;
            })}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900">Runtime snapshots</h2>
          <form onSubmit={(e) => void submitRuntimeSnapshot(e)} className="mt-4 space-y-3">
            <div className="grid gap-3 md:grid-cols-3">
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={snapshotForm.snapshotType} onChange={(e) => setSnapshotForm((prev) => ({ ...prev, snapshotType: e.target.value }))}>
                {SNAPSHOT_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={snapshotForm.healthStatus} onChange={(e) => setSnapshotForm((prev) => ({ ...prev, healthStatus: e.target.value }))}>
                {HEALTH.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={snapshotForm.alertLevel} onChange={(e) => setSnapshotForm((prev) => ({ ...prev, alertLevel: e.target.value }))}>
                {ALERT_LEVELS.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Adoption score 0-100" value={snapshotForm.adoptionScore} onChange={(e) => setSnapshotForm((prev) => ({ ...prev, adoptionScore: e.target.value }))} />
              <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Friction score 0-100" value={snapshotForm.frictionScore} onChange={(e) => setSnapshotForm((prev) => ({ ...prev, frictionScore: e.target.value }))} />
            </div>
            <textarea className="min-h-[96px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Summary" value={snapshotForm.summary} onChange={(e) => setSnapshotForm((prev) => ({ ...prev, summary: e.target.value }))} />
            <textarea className="min-h-[120px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Recommendations, one per line" value={snapshotForm.recommendations} onChange={(e) => setSnapshotForm((prev) => ({ ...prev, recommendations: e.target.value }))} />
            <button disabled={snapshotSaving} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              <NotebookPen className="h-4 w-4" />
              Add runtime snapshot
            </button>
          </form>
          <div className="mt-5 space-y-3">
            {runtimeSnapshots.length === 0 ? <EmptyState label="No runtime snapshots yet" /> : runtimeSnapshots.map((snapshot) => (
              <div key={snapshot.id} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-slate-900">{snapshot.snapshot_type}</div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{snapshot.alert_level}</span>
                </div>
                <div className="mt-2 text-sm text-slate-500">Health {snapshot.health_status} · Adoption {snapshot.adoption_score ?? '-'} · Friction {snapshot.friction_score ?? '-'}</div>
                {snapshot.summary ? <p className="mt-2 text-sm leading-6 text-slate-600">{snapshot.summary}</p> : null}
                {snapshot.recommendations && snapshot.recommendations.length > 0 ? (
                  <div className="mt-3 space-y-1">
                    {snapshot.recommendations.map((item) => <div key={item} className="text-sm text-slate-600">{item}</div>)}
                  </div>
                ) : null}
                <div className="mt-3 text-xs text-slate-400">{new Date(snapshot.captured_at).toLocaleString('en-SG')}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900">Stage gates</h2>
          <form onSubmit={(e) => void submitStageGate(e)} className="mt-4 space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={gateForm.gateType} onChange={(e) => setGateForm((prev) => ({ ...prev, gateType: e.target.value }))}>
                {GATE_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={gateForm.verdict} onChange={(e) => setGateForm((prev) => ({ ...prev, verdict: e.target.value }))}>
                {GATE_VERDICTS.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </div>
            <input type="date" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={gateForm.dueDate} onChange={(e) => setGateForm((prev) => ({ ...prev, dueDate: e.target.value }))} />
            <textarea className="min-h-[96px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Gate summary" value={gateForm.summary} onChange={(e) => setGateForm((prev) => ({ ...prev, summary: e.target.value }))} />
            <textarea className="min-h-[120px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Evidence URLs, one per line" value={gateForm.evidence} onChange={(e) => setGateForm((prev) => ({ ...prev, evidence: e.target.value }))} />
            <button disabled={gateSaving} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              <NotebookPen className="h-4 w-4" />
              Add stage gate
            </button>
          </form>
          <div className="mt-5 space-y-3">
            {stageGates.length === 0 ? <EmptyState label="No stage gates yet" /> : stageGates.map((gate) => (
              <div key={gate.id} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-slate-900">{gate.gate_type}</div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{gate.verdict}</span>
                </div>
                {gate.summary ? <p className="mt-2 text-sm leading-6 text-slate-600">{gate.summary}</p> : null}
                {gate.evidence && gate.evidence.length > 0 ? <div className="mt-2 text-sm text-slate-500">Evidence: {gate.evidence.length}</div> : null}
                <div className="mt-3 text-xs text-slate-400">Due {gate.due_date ?? '-'} · Updated {new Date(gate.updated_at).toLocaleString('en-SG')}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900">Events</h2>
          <div className="mt-4 space-y-3">
            {events.length === 0 ? <EmptyState label="No events yet" /> : events.map((event) => (
              <div key={event.id} className="rounded-2xl border border-slate-200 p-4">
                <div className="font-medium text-slate-900">{event.event_type}</div>
                <div className="mt-1 text-sm text-slate-500">{event.from_stage ?? '-'} → {event.to_stage ?? '-'}</div>
                {event.note ? <p className="mt-2 text-sm leading-6 text-slate-600">{event.note}</p> : null}
                <div className="mt-3 text-xs text-slate-400">{new Date(event.created_at).toLocaleString('en-SG')}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function MetricCard({ label, value, tone = 'bg-white text-slate-900' }: { label: string; value: string; tone?: string }) {
  return (
    <div className={`rounded-2xl border border-slate-200 p-5 ${tone}`}>
      <div className="text-xs font-medium uppercase tracking-[0.22em] opacity-70">{label}</div>
      <div className="mt-3 text-xl font-semibold">{value}</div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-sm text-slate-500">{label}</div>;
}
